import { inject, Injectable } from '@angular/core';

import { Actions, ofType } from '@ngrx/effects';
import { TranslateService } from '@ngx-translate/core';

import { HelpContentState } from './help.models';
import { HelpApiService } from 'src/app/api/services/help-api.service';

import { setCoreLanguageSuccess } from '../../core/state/core.actions';

import { Observable, of, BehaviorSubject } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';

// TODO: maybe we need to keep the translation keys in the FE registry?
// TODO: for now we keep vanilla strings - we need to discuss this
const LOCAL_CONTENT_REGISTRY: Record<string, string> = {};

// * loading and caching help content
@Injectable({
  providedIn: 'root',
})
export class HelpContentService {
  private contentCache = new Map<string, BehaviorSubject<HelpContentState>>();

  private translate = inject(TranslateService);
  private helpApi = inject(HelpApiService);
  private actions$ = inject(Actions);

  constructor() {
    this.actions$
      .pipe(
        ofType(setCoreLanguageSuccess),
        tap(() => {
          this.clearCache();
        }),
      )
      .subscribe();
  }

  getContent(key: string): Observable<HelpContentState> {
    if (this.contentCache.has(key)) {
      return this.contentCache.get(key)!.asObservable();
    }

    // * create new cache entry with loading state
    const stateSubject = new BehaviorSubject<HelpContentState>({
      status: 'loading',
    });
    this.contentCache.set(key, stateSubject);

    // * second -> check Local Registry first
    if (LOCAL_CONTENT_REGISTRY[key]) {
      stateSubject.next({
        status: 'success',
        // TODO: maybe we need to keep the translation keys in the FE registry?
        // TODO: for now we keep vanilla strings - we need to discuss this
        // content: this.translate.instant(LOCAL_CONTENT_REGISTRY[key]),
        content: LOCAL_CONTENT_REGISTRY[key],
      });
      return stateSubject.asObservable();
    }

    // * fallback -> load from API
    this.loadFromApi(key, stateSubject);

    return stateSubject.asObservable();
  }

  private loadFromApi(
    key: string,
    stateSubject: BehaviorSubject<HelpContentState>,
  ): void {
    this.helpApi
      .getContent(key)
      .pipe(
        map((response) => ({
          status: 'success' as const,
          content: response.content,
        })),
        catchError((error) => {
          // ! remove from cache on error - allow retry on next attempt
          this.contentCache.delete(key);

          return of({
            status: 'error' as const,
            error: this.translate.instant('help.content-unavailable'),
          });
        }),
      )
      .subscribe((state) => stateSubject.next(state));
  }

  // ! Eventually we shall clear the cache in Language change, maybe there are other cases too...
  clearCache(key?: string): void {
    if (key) {
      this.contentCache.delete(key);
    } else {
      this.contentCache.clear();
    }
  }
}
